import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = {
  class: "date-time-selection-modal-content",
  "data-cy": "date-time-selection-modal"
}
const _hoisted_2 = { class: "date-time-group" }
const _hoisted_3 = { class: "date-time-group" }
const _hoisted_4 = { class: "d-flex" }
const _hoisted_5 = { class: "m-alert-icon" }
const _hoisted_6 = { class: "m-alert-content" }
const _hoisted_7 = { class: "date-time-group" }
const _hoisted_8 = { class: "footer" }
const _hoisted_9 = { class: "footer-result" }
const _hoisted_10 = { class: "absolute" }
const _hoisted_11 = { class: "footer-actions" }

import MDateAndTimePicker from "@/BasicComponentLibrary/MDateAndTimePicker.vue";
import _ from "lodash";
import Icon from "@common/components/Icon.vue";
import { computed, reactive, withDefaults, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getRelativeDate, RelativeTimeOption, relativeTimeOptions } from "@util/relativeDate";
import Alert from "@common/components/Alert.vue";

//#region General
type ViewModel = {
  isRelative: boolean | null;
  selectedDateTime: string;
  selectedRelativeOption: RelativeTimeOption | undefined;
  selectedRelativeOptionValue: number;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'DateTimeSelectionModal',
  props: {
    isOpen: { type: Boolean },
    isRelative: { type: [Boolean, null], default: false },
    canBeNone: { type: Boolean, default: false },
    time: { default: "" },
    optionKey: { default: "today" },
    optionValue: { default: 1 }
  },
  emits: ["update:isOpen", "confirm", "cancelSelection"],
  setup(__props: any, { emit }) {

const props = __props;




const { t: $t, d: $d } = useI18n();
//#endregion

//#region ViewModel
const vm = reactive<ViewModel>({
  isRelative: props.isRelative,
  selectedDateTime: props.time,
  selectedRelativeOption: relativeTimeOptions.find((o) => o.key === props.optionKey),
  selectedRelativeOptionValue: props.optionValue
});

const nav = computed(() => {
  const initialOptions = [
    {
      text: $t("Fixed"),
      value: false
    },
    {
      text: $t("Relative"),
      value: true
    }
  ];

  if (!props.canBeNone) {
    return initialOptions;
  }

  const noneOption = {
    text: $t("None"),
    value: null
  };

  return [...initialOptions, noneOption];
});
//#endregion

//#region Localisations
function translate(option: RelativeTimeOption | undefined): string {
  if (!option || option.key === "") return "";
  const localKey = "relativeDateTimePicker.";
  return $t(localKey + option.key);
}
//#endregion

//#region Relative Time
const groupedOptions = computed(() => {
  const groups = new Map<number, RelativeTimeOption[]>();

  for (const option of relativeTimeOptions) {
    if (!groups.has(option.groupId)) {
      groups.set(option.groupId, []);
    }
    groups.get(option.groupId)!.push(option);
  }

  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([groupId, options]) => ({
      groupId,
      options: options.sort((a, b) => a.sequence - b.sequence)
    }));
});

const lastGroupId = computed(() => Math.max(...groupedOptions.value.map((group) => group.groupId)));

const selectOption = (option: RelativeTimeOption) => {
  vm.selectedRelativeOption = option;
  if (vm.selectedRelativeOption.type !== "singleVar") vm.selectedRelativeOptionValue = 1;
};
//#endregion

//#region Result
const selectionPreview = computed(() => {
  if (vm.isRelative === null) return `<${$t("empty")}>`;
  else if (vm.isRelative) {
    if (validationFeedback.value) return "-";
    const result = getRelativeDate({ key: vm.selectedRelativeOption?.key, value: vm.selectedRelativeOptionValue });
    return result.display;
  } else {
    if (_.isEmpty(vm.selectedDateTime)) return $t("SpecifyTime");
    const containsOnlyDate = vm.selectedDateTime.split(" ").length === 1 ? true : false;
    return getDay() + ", " + $d(vm.selectedDateTime, containsOnlyDate ? "shortDate" : "shortDateTime");
  }
});

function getDay(): string {
  const day = new Date(vm.selectedDateTime).getDay();
  const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][day];
  return $t("datepicker." + dayName);
}
//#endregion

//#region Updatable Props
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      vm.isRelative = props.isRelative;
      vm.selectedDateTime = props.time;
      vm.selectedRelativeOption = relativeTimeOptions.find((o) => o.key === props.optionKey);
      vm.selectedRelativeOptionValue = props.optionValue;
    }
  }
);

const localIsOpen = computed({
  get() {
    return props.isOpen;
  },
  set(nv) {
    emit("update:isOpen", nv);
  }
});
//#endregion

const onOk = () => {
  let value: string | undefined = "";

  if (vm.isRelative !== null) {
    if (!vm.isRelative) value = vm.selectedDateTime;
    else {
      value =
        vm.selectedRelativeOptionValue > 1
          ? vm.selectedRelativeOption?.backendKey?.plural?.replace("{value}", vm.selectedRelativeOptionValue.toString())
          : vm.selectedRelativeOption?.backendKey?.singular;
    }
  }
  emit("confirm", value ?? "");
};
const onCancel = (event: Event & { trigger: string }) => {
  if (event.trigger !== "ok") {
    emit("cancelSelection");
  }
};
//#endregion

//#region Validation
const validationFeedback: Record<string, any> = computed(() => {
  if (vm.isRelative === false) {
    return vm.selectedDateTime === "" ? $t("isMandatoryValue") : "";
  } else if (vm.isRelative === true) {
    if (vm.selectedRelativeOptionValue < 1) return $t("isMandatoryValue");
    const result = Number(vm.selectedRelativeOptionValue);
    return vm.isRelative === true && typeof result === "number" && Number.isFinite(result) ? "" : $t("ValueIsNotNumber");
  }
  return "";
});
//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_form_radio_group = _resolveComponent("b-form-radio-group")!
  const _component_b_form_group = _resolveComponent("b-form-group")!
  const _component_b_dropdown_item = _resolveComponent("b-dropdown-item")!
  const _component_b_dropdown_divider = _resolveComponent("b-dropdown-divider")!
  const _component_b_dropdown = _resolveComponent("b-dropdown")!
  const _component_b_input = _resolveComponent("b-input")!
  const _component_b_alert = _resolveComponent("b-alert")!
  const _component_b_btn = _resolveComponent("b-btn")!
  const _component_b_modal = _resolveComponent("b-modal")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_component_b_modal, {
    title: _unref($t)('DefineDateTime'),
    modelValue: localIsOpen.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localIsOpen).value = $event)),
    id: "date-time-selection-modal",
    size: "md",
    "body-class": "date-time-selection-modal-body",
    "header-close-content": "",
    centered: "",
    onHide: onCancel,
    onOk: onOk
  }, {
    "modal-footer": _withCtx(({ ok, cancel }) => [
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("span", _hoisted_10, _toDisplayString(selectionPreview.value), 1)
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createVNode(_component_b_btn, {
            onClick: ok,
            variant: "primary",
            disabled: validationFeedback.value.length > 0,
            "data-cy": "date-time-selection-confirm-btn"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref($t)("OK")), 1)
            ]),
            _: 2
          }, 1032, ["onClick", "disabled"]),
          _createVNode(_component_b_btn, {
            onClick: cancel,
            "data-cy": "date-time-selection-cancel-btn"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref($t)("Cancel")), 1)
            ]),
            _: 2
          }, 1032, ["onClick"])
        ])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", null, [
          _createElementVNode("strong", null, _toDisplayString(_unref($t)("DateTimeVariationQuestion")), 1),
          _createVNode(_component_b_form_group, { class: "mb-0" }, {
            default: _withCtx(({ ariaDescribedby }) => [
              _createVNode(_component_b_form_radio_group, {
                id: "btn-radios-1",
                modelValue: vm.isRelative,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((vm.isRelative) = $event)),
                options: nav.value,
                "aria-describedby": ariaDescribedby,
                name: "radios-btn-default",
                class: "radio-buttons",
                buttons: "",
                "data-cy": "date-time-selection-types",
                "button-variant": "primary"
              }, null, 8, ["modelValue", "options", "aria-describedby"])
            ]),
            _: 1
          })
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_2, [
          _createElementVNode("label", null, _toDisplayString(_unref($t)("FixedTime")), 1),
          _createVNode(MDateAndTimePicker, {
            modelValue: vm.selectedDateTime,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((vm.selectedDateTime) = $event)),
            validation: validationFeedback.value
          }, null, 8, ["modelValue", "validation"])
        ], 512), [
          [_vShow, vm.isRelative === false]
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_3, [
          _createElementVNode("label", null, _toDisplayString(_unref($t)("RelativeTime")), 1),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_b_dropdown, {
              class: "dropdown-default w-100",
              "toggle-class": "w-100",
              "data-cy": "relative-date-ddn"
            }, {
              "button-content": _withCtx(() => [
                _createElementVNode("span", null, _toDisplayString(translate(vm.selectedRelativeOption)), 1)
              ]),
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedOptions.value, (group) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: group.groupId
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.options, (option) => {
                      return (_openBlock(), _createBlock(_component_b_dropdown_item, {
                        key: option.key,
                        onClick: ($event: any) => (selectOption(option)),
                        active: option.key === vm.selectedRelativeOption?.key
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(translate(option)), 1)
                        ]),
                        _: 2
                      }, 1032, ["onClick", "active"]))
                    }), 128)),
                    (group.groupId !== lastGroupId.value)
                      ? (_openBlock(), _createBlock(_component_b_dropdown_divider, { key: 0 }))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 128))
              ]),
              _: 1
            }),
            _withDirectives(_createVNode(_component_b_input, {
              type: "number",
              min: "0",
              max: "999",
              step: "1",
              modelValue: vm.selectedRelativeOptionValue,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((vm.selectedRelativeOptionValue) = $event)),
              inputmode: "numeric",
              autocomplete: "off",
              class: _normalizeClass(["relative-value", { 'border-danger': validationFeedback.value }])
            }, null, 8, ["modelValue", "class"]), [
              [_vShow, vm.selectedRelativeOption?.type === 'singleVar'],
              [_directive_tooltip, validationFeedback.value]
            ])
          ]),
          _createElementVNode("div", null, [
            _createVNode(_component_b_alert, {
              class: "m-alert mt-1 p-0",
              show: "",
              variant: "light"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(Icon, {
                    icon: "Info",
                    size: "12px"
                  })
                ]),
                _createElementVNode("div", _hoisted_6, _toDisplayString(_unref($t)("RelativeDateInfoText")), 1)
              ]),
              _: 1
            })
          ])
        ], 512), [
          [_vShow, vm.isRelative === true]
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_7, [
          _createVNode(Alert, {
            show: "",
            "with-icon": ""
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref($t)("EmptyValueSelected")), 1)
            ]),
            _: 1
          })
        ], 512), [
          [_vShow, vm.isRelative === null]
        ])
      ])
    ]),
    _: 1
  }, 8, ["title", "modelValue"]))
}
}

})